import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, ArrowUp } from 'lucide-react';
import { portalRoot } from './TourGuide';

const HFOV = 50, VFOV = 66, EYE = 1.5;
const cmTxt = (v) => (Math.abs(v) < 1 ? `${Math.round(Math.abs(v) * 100)} cm` : `${Math.abs(v).toFixed(2).replace('.', ',')} m`);

// compass heading of the back camera (W3C formula) + camera elevation from the tilt
function useCamPose() {
  const [pose, setPose] = useState(null);
  useEffect(() => {
    const ev = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
    let last = 0;
    const on = (e) => {
      const now = Date.now(); if (now - last < 80 || e.beta == null) return; last = now;
      let h = null;
      if (e.webkitCompassHeading != null) h = e.webkitCompassHeading;
      else if (e.alpha != null && (e.absolute || e.type === 'deviceorientationabsolute')) {
        const r = Math.PI / 180, a = e.alpha * r, b = e.beta * r, g = (e.gamma || 0) * r;
        const rA = -Math.cos(a) * Math.sin(g) - Math.sin(a) * Math.sin(b) * Math.cos(g), rB = -Math.sin(a) * Math.sin(g) + Math.cos(a) * Math.sin(b) * Math.cos(g);
        h = ((Math.atan2(rA, rB) * 180) / Math.PI + 360) % 360;
      }
      setPose({ h, elev: e.beta - 90 });
    };
    window.addEventListener(ev, on);
    return () => window.removeEventListener(ev, on);
  }, []);
  return pose;
}

// AR stake-out: live camera, direction arrow, distance and a target marker where the point is in the image
export default function ArStake({ t, name, d, brg, tol, live, blocked, onLog, onClose }) {
  const vid = useRef(null);
  const [camErr, setCamErr] = useState(false);
  const pose = useCamPose();
  useEffect(() => {
    let s = null, alive = true;
    navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((x) => { if (!alive) { x.getTracks().forEach((k) => k.stop()); return; } s = x; vid.current.srcObject = x; })
      .catch(() => setCamErr(true));
    if (!navigator.mediaDevices?.getUserMedia) setCamErr(true);
    return () => { alive = false; s?.getTracks().forEach((k) => k.stop()); };
  }, []);
  const h = pose?.h, rel = h != null && d != null ? ((brg - h + 540) % 360) - 180 : null;
  const reached = d != null && d <= tol.h;
  const tElev = d != null ? -(Math.atan2(EYE, Math.max(d, 0.05)) * 180) / Math.PI : 0;
  const mx = rel != null ? 50 + (rel / HFOV) * 100 : 50, my = 50 - ((tElev - (pose?.elev ?? -30)) / VFOV) * 100;
  const inView = rel != null && Math.abs(rel) < HFOV / 2 + 4 && my > 2 && my < 98;
  const ms = Math.max(28, Math.min(140, 90 / Math.max(d || 1, 0.3)));
  const fwd = rel != null ? d * Math.cos((rel * Math.PI) / 180) : null, right = rel != null ? d * Math.sin((rel * Math.PI) / 180) : null;
  return createPortal(
    <div className="ar-stake" data-testid="ar-stake">
      <video ref={vid} autoPlay playsInline muted className="ar-video" data-testid="ar-video" />
      {camErr && <p className="ar-msg" data-testid="ar-no-cam">{t.arNoCam}</p>}
      {inView && !reached && <div className="ar-marker" data-testid="ar-marker" style={{ left: `${mx}%`, top: `${my}%`, width: ms, height: ms }}><i /></div>}
      <div className="ar-top">
        <b data-testid="ar-target-name">🎯 {name}</b>
        <button className="icon-btn" data-testid="ar-close-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className={`ar-hud ${reached ? 'ok' : ''}`} data-testid="ar-hud">
        {reached ? <b className="ar-reached" data-testid="ar-reached"><CheckCircle2 size={28} /> {t.arReached}</b> : (
          <>
            <span className="ar-arrow" data-testid="ar-arrow" style={{ transform: `rotate(${rel ?? brg}deg)` }}><ArrowUp size={64} strokeWidth={3} /></span>
            <b className="ar-dist" data-testid="ar-dist">{d != null ? cmTxt(d) : '–'}</b>
            {fwd != null && d < 3 && <small data-testid="ar-offsets">{cmTxt(fwd)} {fwd >= 0 ? t.arFwd : t.arBack} · {cmTxt(right)} {right >= 0 ? t.arRight : t.arLeft}</small>}
          </>
        )}
        {h == null ? <small className="ar-warn" data-testid="ar-no-head">{t.arNoHead}</small> : <small className="muted">{t.arHint}</small>}
        <button className="btn btn-green" data-testid="ar-log-btn" disabled={!live || blocked} onClick={onLog}>✅ {t.skLog}</button>
      </div>
    </div>,
    portalRoot(),
  );
}
